/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';
import { z } from 'zod';

import { Toast } from 'sharedUi/Toast';
import PageSkeleton from '../../components/common/page-skeleton';
import ScreenHeader from '../../components/common/screen-header';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { Input } from 'sharedUi/Input';
import { TextArea } from 'sharedUi/TextArea';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import DiscardChangesDialog from '../../components/dialogs/discard-changes-dialog';
import FormSelect from '../../components/forms/form-select';

import FormMultiSelect from '../../components/forms/form-multi-select';

import { promptsService } from '../../services/prompts.service';
import { projectsService } from '../../services/projects.service';
import { aiModelsService } from '../../services/ai-models.service';
import { evalSuitesService } from '../../services/eval-suites.service';
import { extractErrorMessage } from '../../utils/error-handler';

// Zod Validation Schema
const promptSchema = z.object({
  type: z
    .string()
    .min(1, 'Type is required')
    .max(200, 'Type must not exceed 200 characters')
    .trim(),
  name: z
    .string()
    .min(1, 'Name is required')
    .max(200, 'Name must not exceed 200 characters')
    .trim(),
  aiRole: z
    .string()
    .min(1, 'AI Role is required')
    .trim(),
  systemRole: z
    .string()
    .min(1, 'System Role is required')
    .trim(),
  objective: z
    .string()
    .min(1, 'Objective is required')
    .trim(),
  iconPath: z.string().max(500, 'Icon Path must not exceed 500 characters').optional().or(z.literal('')),
  tech: z.string().optional().or(z.literal('')),
  taskInstructions: z.string().optional().or(z.literal('')),
  taskInput: z.string().optional().or(z.literal('')),
  taskOutputFormat: z.string().optional().or(z.literal('')),
  taskExample: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine(
      (val) => {
        if (!val || val === '') return true;
        try {
          JSON.parse(val);
          return true;
        } catch {
          return false;
        }
      },
      { message: 'Task Example must be valid JSON' }
    ),
  llm: z.string().max(100, 'LLM must not exceed 100 characters').optional().or(z.literal('')),
  modelId: z.string().optional().or(z.literal('')),
  evalIds: z.array(z.string()).optional(),
  appId: z.string().trim().optional().or(z.literal('')),
  settingsJson: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine(
      (val) => {
        if (!val || val === '') return true;
        try {
          JSON.parse(val);
          return true;
        } catch {
          return false;
        }
      },
      { message: 'Settings JSON must be valid JSON' }
    ),
});

type PromptFormData = z.infer<typeof promptSchema>;

const PromptForm = () => {
  const { id, isReady, navigateToList, navigateToView } = useSecureNavigation({
    entity: 'prompt',
    basePath: '/prompt-table',
  });
  const isEditMode = Boolean(id);
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);
  // Stops autosave rewriting the draft after a submit clears it (the reason the
  // "restore?" banner reappeared right after a create/update).
  const submittedRef = useRef(false);

  const DRAFT_KEY = `prompt-draft-${isEditMode ? id : 'new'}`;

  // Versioning: why this change is made (required in edit mode — it goes in the
  // change log) and whether it is published now or kept as the draft.
  const [changeNote, setChangeNote] = useState('');
  const [noteError, setNoteError] = useState('');
  const saveAsRef = useRef<'ACTIVE' | 'DRAFT'>('ACTIVE');

  // Draft state
  const [draftData, setDraftData] = useState<PromptFormData | null>(null);
  const [showDraftBanner, setShowDraftBanner] = useState(false);

  // React Hook Form
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<PromptFormData>({
    resolver: zodResolver(promptSchema),
    defaultValues: {
      type: '',
      name: '',
      aiRole: '',
      systemRole: '',
      objective: '',
      iconPath: '',
      tech: '',
      taskInstructions: '',
      taskInput: '',
      taskOutputFormat: '',
      taskExample: '',
      llm: '',
      modelId: '',
      evalIds: [],
      appId: '',
      settingsJson: '',
    },
    mode: 'onChange',
  });

  // Projects the caller may create in (platform admin = all, else own).
  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 5 * 60 * 1000,
  });
  const projectOptions = accessibleProjects.map((p) => ({
    value: p._id,
    label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
  }));
  const projectLocked = accessibleProjects.length <= 1;
  const selectedAppId = watch('appId');
  useEffect(() => {
    if (!isEditMode && !selectedAppId && accessibleProjects.length) {
      setValue('appId', accessibleProjects[0]._id);
    }
  }, [isEditMode, selectedAppId, accessibleProjects, setValue]);

  const { showLeaveDialog, confirmLeave, cancelLeave, handleNavigate } = useNavigationBlocker({ when: isDirty });

  const formValues = watch();

  // Fetch prompt data for edit mode - wait for ID decryption
  const { data: prompt, isLoading: fetchLoading } = useQuery({
    queryKey: ['prompts', id],
    queryFn: () => promptsService.getById(id!),
    enabled: isEditMode && isReady && !!id,
  });

  // A draft in progress is what gets edited — the live prompt's text is the
  // active version, and reopening Edit must continue the draft, not reset it.
  const draftVersion = isEditMode ? prompt?.draftVersion ?? null : null;
  const { data: draft, isLoading: draftLoading } = useQuery({
    queryKey: ['prompt-versions', id, draftVersion],
    queryFn: () => promptsService.getVersion(id!, draftVersion!),
    enabled: draftVersion != null && !!id,
  });

  // Fetch AI models for the Model dropdown (options = displayName, value = _id),
  // scoped to the selected project (BE also includes global/shared models).
  const { data: aiModelsResponse, isLoading: modelsLoading } = useQuery({
    // runnable_only: a prompt names the model it runs against, so an embedding
    // model in this list could only ever be a choice that fails.
    queryKey: ['ai-models', 'options', 'runnable', selectedAppId || 'all'],
    queryFn: () => aiModelsService.list({
      page: 1, page_size: 100, app_id: selectedAppId || undefined, runnable_only: true,
    }),
    staleTime: 5 * 60 * 1000,
  });
  const modelOptions = (aiModelsResponse?.data || []).map((m) => ({
    label: m.displayName,
    value: m._id,
  }));

  // Fetch prompt-type evals for the Evals multi-select (options = name, value = _id),
  // scoped to the selected project (BE also includes global/shared evals).
  const { data: evalsResponse, isLoading: evalsLoading } = useQuery({
    queryKey: ['eval-suites', 'options', 'prompt', selectedAppId || 'all'],
    queryFn: () => evalSuitesService.list({ type: 'prompt', page: 1, page_size: 100, app_id: selectedAppId || undefined }),
    staleTime: 5 * 60 * 1000,
  });
  const evalOptions = (evalsResponse?.data || []).map((e) => ({
    label: e.name,
    value: e._id,
  }));

  // Set breadcrumb title to show prompt name instead of UUID in edit mode
  useBreadcrumbTitle(isEditMode ? prompt?.name : null);

  // Populate form when prompt data is loaded
  useEffect(() => {
    if (prompt && isEditMode && (draftVersion == null || draft)) {
      // Details from the prompt; content from the draft when there is one.
      const c: any = draft ? { ...prompt, ...draft.content } : prompt;
      reset({
        type: prompt.type || '',
        name: prompt.name || '',
        aiRole: c.aiRole || '',
        systemRole: c.systemRole || '',
        objective: c.objective || '',
        iconPath: prompt.iconPath || '',
        tech: prompt.tech || '',
        taskInstructions: c.taskInstructions || '',
        taskInput: c.taskInput || '',
        taskOutputFormat: c.taskOutputFormat || '',
        taskExample: c.taskExample ? JSON.stringify(c.taskExample, null, 2) : '',
        llm: c.llm || '',
        modelId: c.modelId || '',
        evalIds: prompt.evalIds || [],
        appId: (prompt as any).appId || '',
        settingsJson: c.settingsJson ? JSON.stringify(c.settingsJson, null, 2) : '',
      });
    }
  }, [prompt, draft, draftVersion, isEditMode, reset]);

  // Draft autosave (only for create mode)
  useEffect(() => {
    if (isEditMode || !isDirty || submittedRef.current) return;

    const timer = setTimeout(() => {
      if (submittedRef.current) return;
      localStorage.setItem(DRAFT_KEY, JSON.stringify(formValues));
    }, 1500);

    return () => clearTimeout(timer);
  }, [formValues, isDirty, isEditMode, DRAFT_KEY]);

  // Load draft on mount (create mode only)
  useEffect(() => {
    if (!isEditMode) {
      const savedDraft = localStorage.getItem(DRAFT_KEY);
      if (savedDraft) {
        try {
          const parsed = JSON.parse(savedDraft);
          setDraftData(parsed);
          setShowDraftBanner(true);
        } catch (error) {
          console.error('Failed to parse draft:', error);
        }
      }
    }
  }, [isEditMode, DRAFT_KEY]);

  // Mutation
  const mutation = useMutation({
    mutationFn: (data: any) => {
      if (isEditMode && id) {
        return promptsService.update(id, data);
      }
      return promptsService.create(data);
    },
    onSuccess: (saved: any) => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
      queryClient.invalidateQueries({ queryKey: ['prompt-versions'] });
      queryClient.invalidateQueries({ queryKey: ['prompt-change-log'] });
      localStorage.removeItem(DRAFT_KEY);
      const asDraft = isEditMode && saveAsRef.current === 'DRAFT';
      toast.current?.show({
        severity: 'success',
        summary: asDraft ? 'Draft saved' : isEditMode ? 'Published' : 'Success',
        detail: !isEditMode ? 'Prompt created successfully'
          : asDraft ? `Draft v${saved?.draftVersion ?? ''} saved. The live prompt is unchanged.`
            : `v${saved?.version ?? ''} is now the active version.`,
        life: 3000,
      });
      // Back to the prompt, where its versions and change log are.
      setTimeout(() => {
        const target = isEditMode ? id : saved?._id;
        if (target) navigateToView(target); else navigateToList();
      }, 1000);
    },
    onError: (error: any) => {
      submittedRef.current = false;
      const errorMessage = extractErrorMessage(error, `Failed to ${isEditMode ? 'update' : 'create'} prompt`);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 10000,
      });
    },
  });

  // Combined loading state for button disable
  const isSaving = mutation.isPending;

  const onSubmit = (data: PromptFormData) => {
    if (isEditMode && !changeNote.trim()) {
      setNoteError('Say what changed and why — it is recorded in the change log.');
      return;
    }
    submittedRef.current = true;
    localStorage.removeItem(DRAFT_KEY);
    const payload: any = {
      type: data.type,
      name: data.name,
      aiRole: data.aiRole,
      systemRole: data.systemRole,
      objective: data.objective,
      iconPath: data.iconPath || null,
      tech: data.tech || null,
      taskInstructions: data.taskInstructions || null,
      taskInput: data.taskInput || null,
      taskOutputFormat: data.taskOutputFormat || null,
      taskExample: data.taskExample ? JSON.parse(data.taskExample) : null,
      llm: data.llm || null,
      modelId: data.modelId || null,
      evalIds: data.evalIds || [],
      settingsJson: data.settingsJson ? JSON.parse(data.settingsJson) : null,
    };

    // Owning project (honoured by the BE only for callers who can access it).
    if (data.appId) payload.appId = data.appId;
    if (isEditMode) {
      payload.changeNote = changeNote.trim();
      payload.saveAs = saveAsRef.current;
    }

    try {
      mutation.mutate(payload);
    } catch (error) {
      console.error('Error calling mutation.mutate:', error);
    }
  };

  const handleLoadDraft = () => {
    if (draftData) {
      reset(draftData);
      setShowDraftBanner(false);
    }
  };

  const handleDiscardDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setDraftData(null);
    setShowDraftBanner(false);
  };

  // Wait for the draft too, or the form would open on the live text and then jump.
  if (fetchLoading || (draftVersion != null && draftLoading)) {
    return (
      <PageSkeleton />
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900" data-testid="SCR-Prompt-Form">
        <ScreenHeader
          icon="pi pi-comments"
          onBack={() => handleNavigate('/prompt-table')}
          title={isEditMode ? 'Edit Prompt' : 'Create New Prompt'}
          subtitle={isEditMode ? 'Update prompt configuration' : 'Configure a new AI prompt template'}
        />

        {/* Draft Banner */}
        {showDraftBanner && draftData && (
          <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="pi pi-exclamation-triangle text-amber-600" />
              <span className="text-sm text-amber-800 dark:text-amber-200">
                You have unsaved changes from a previous session. Would you like to restore them?
              </span>
            </div>
            <div className="flex gap-2">
              <Button label="Restore" size="small" onClick={handleLoadDraft} />
              <Button label="Discard" size="small" severity="secondary" outlined onClick={handleDiscardDraft} />
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Project Scope */}
          <div className="ds-card mb-4">
            <div className="px-4 py-3 border-b border-line">
              <div className="flex items-center gap-2">
                <i className="pi pi-sitemap text-blue-600" />
                <span className="text-sm font-medium text-ink">Project Scope</span>
              </div>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <Controller
                    name="appId"
                    control={control}
                    render={({ field: { value, onChange, ...field } }) => (
                      <FormSelect
                        {...field}
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        label="Project"
                        options={projectOptions}
                        placeholder="Select project"
                        disabled={projectLocked || isEditMode}
                        error={!!errors.appId}
                        helperText={errors.appId?.message}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div className="ds-card mb-4">
            <div className="px-4 py-3 border-b border-line">
              <div className="flex items-center gap-2">
                <i className="pi pi-info-circle text-blue-600" />
                <span className="text-sm font-medium text-ink">Basic Information</span>
              </div>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Type <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="type"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="e.g., text, code, data-analysis"
                        error={errors.type?.message}
                      />
                    )}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="Unique prompt name"
                        error={errors.name?.message}
                      />
                    )}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    AI Role <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="aiRole"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="e.g., Code Assistant, Data Analyst"
                        error={errors.aiRole?.message}
                      />
                    )}
                  />
                </div>

                <div className="md:col-span-2 flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    System Role <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="systemRole"
                    control={control}
                    render={({ field }) => (
                      <TextArea
                        {...field}
                        placeholder="System's expected actions or interactions"
                        rows={3}
                        error={errors.systemRole?.message}
                      />
                    )}
                  />
                </div>

                <div className="md:col-span-2 flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Objective <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="objective"
                    control={control}
                    render={({ field }) => (
                      <TextArea
                        {...field}
                        placeholder="Main goal or function of the prompt"
                        rows={3}
                        error={errors.objective?.message}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Configuration */}
          <div className="ds-card mb-4">
            <div className="px-4 py-3 border-b border-line">
              <div className="flex items-center gap-2">
                <i className="pi pi-cog text-blue-600" />
                <span className="text-sm font-medium text-ink">Configuration</span>
              </div>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Model
                  </label>
                  <Controller
                    name="modelId"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        value={field.value || null}
                        onChange={(e: { value: string }) => {
                          field.onChange(e.value || '');
                          // Keep the denormalized `llm` label in step with the chosen
                          // model (modelId is the source of truth). This is what the
                          // list/detail show, so it must reflect the selected model.
                          const row = (aiModelsResponse?.data || []).find((m: any) => m._id === e.value);
                          setValue('llm', row?.modelId || '', { shouldDirty: true });
                        }}
                        options={modelOptions}
                        placeholder={modelsLoading ? 'Loading...' : 'Select model'}
                        disabled={modelsLoading}
                        filter
                        showClear
                      />
                    )}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <Controller
                    name="evalIds"
                    control={control}
                    render={({ field }) => (
                      <FormMultiSelect
                        name="evalIds"
                        label="Evals"
                        value={field.value || []}
                        onChange={(value) => field.onChange(value)}
                        options={evalOptions}
                        placeholder="Select evals"
                        loading={evalsLoading}
                      />
                    )}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Tech
                  </label>
                  <Controller
                    name="tech"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="Technology stack"
                        error={errors.tech?.message}
                      />
                    )}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Icon Path
                  </label>
                  <Controller
                    name="iconPath"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="File or asset path to an icon"
                        error={errors.iconPath?.message}
                      />
                    )}
                  />
                </div>

              </div>
            </div>
          </div>

          {/* Task Details */}
          <div className="ds-card mb-4">
            <div className="px-4 py-3 border-b border-line">
              <div className="flex items-center gap-2">
                <i className="pi pi-list text-blue-600" />
                <span className="text-sm font-medium text-ink">Task Details</span>
              </div>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Task Instructions
                  </label>
                  <Controller
                    name="taskInstructions"
                    control={control}
                    render={({ field }) => (
                      <TextArea
                        {...field}
                        placeholder="Steps or logic flow to execute the task"
                        rows={4}
                        error={errors.taskInstructions?.message}
                      />
                    )}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Task Input
                  </label>
                  <Controller
                    name="taskInput"
                    control={control}
                    render={({ field }) => (
                      <TextArea
                        {...field}
                        placeholder="Required inputs or parameters"
                        rows={3}
                        error={errors.taskInput?.message}
                      />
                    )}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Task Output Format
                  </label>
                  <Controller
                    name="taskOutputFormat"
                    control={control}
                    render={({ field }) => (
                      <TextArea
                        {...field}
                        placeholder="Expected structure or data format of output"
                        rows={3}
                        error={errors.taskOutputFormat?.message}
                      />
                    )}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Task Example (JSON)
                  </label>
                  <Controller
                    name="taskExample"
                    control={control}
                    render={({ field }) => (
                      <TextArea
                        {...field}
                        placeholder='{"input": "example", "output": "result"}'
                        rows={4}
                        error={errors.taskExample?.message}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Settings */}
          <div className="ds-card mb-4">
            <div className="px-4 py-3 border-b border-line">
              <div className="flex items-center gap-2">
                <i className="pi pi-sliders-h text-blue-600" />
                <span className="text-sm font-medium text-ink">Settings</span>
              </div>
            </div>
            <div className="p-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Settings JSON
                </label>
                <Controller
                  name="settingsJson"
                  control={control}
                  render={({ field }) => (
                    <TextArea
                      {...field}
                      placeholder='{"temperature": 0.7, "maxTokens": 1000}'
                      rows={5}
                      error={errors.settingsJson?.message}
                    />
                  )}
                />
              </div>
            </div>
          </div>

          {/* Form Errors */}
          {Object.keys(errors).length > 0 && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
              <i className="pi pi-exclamation-circle text-red-600" />
              <span className="text-sm text-red-800 dark:text-red-200">
                Please fix the errors above before submitting.
              </span>
            </div>
          )}

          {/* Versioning, said before the first save. */}
          {!isEditMode && (
            <div className="ds-card mb-4 p-3" data-testid="PromptForm.VersioningNew">
              <h2 className="text-sm font-semibold text-ink">Version</h2>
              <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                This prompt is saved as <strong>v1 — the active version</strong>. Later edits can be saved as a draft
                and published when ready; every version is kept and every change goes in the change log.
              </p>
            </div>
          )}

          {/* Versioning: every edit is recorded, and published or kept as the draft. */}
          {isEditMode && (
            <div className="ds-card mb-4 p-3" data-testid="PromptForm.Versioning">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-sm font-semibold text-ink">Version</h2>
                <span className="text-xs text-ink-muted">
                  {draftVersion != null
                    ? `Editing draft v${draftVersion}. Live: v${prompt?.version ?? 1}.`
                    : `Live: v${prompt?.version ?? 1}. A content change becomes v${(prompt?.version ?? 1) + 1}.`}
                </span>
              </div>
              <TextArea
                label="What changed, and why"
                required
                value={changeNote}
                onChange={(v) => { setChangeNote(v); setNoteError(''); }}
                rows={2}
                placeholder="e.g. Answers were too long for the support widget — capped at two sentences"
                error={noteError || undefined}
                testId="PromptForm.ChangeNote"
              />
              <p className="mt-1 text-xs text-ink-muted">
                <strong>Save as draft</strong> keeps the change as the draft version — every run, eval and RAG answer
                keeps using the active version. <strong>Publish</strong> makes it the active version now.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 justify-end">
            <Button
              type="button"
              label="Cancel"
              severity="secondary"
              outlined
              onClick={() => handleNavigate('/prompt-table')}
              disabled={isSaving}
            />
            {isEditMode && (
              <Button
                type="submit"
                label="Save as draft"
                icon="pi pi-file-edit"
                severity="secondary"
                outlined
                disabled={isSaving}
                onClick={() => { saveAsRef.current = 'DRAFT'; }}
                data-testid="PromptForm.Button.SaveDraft"
              />
            )}
            <Button
              type="submit"
              label={isSaving ? 'Saving...' : isEditMode ? 'Publish' : 'Create'}
              icon={isSaving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
              disabled={isSaving}
              onClick={() => { saveAsRef.current = 'ACTIVE'; }}
              data-testid="PromptForm.Button.Submit"
            />
          </div>
        </form>
      </div>

      {/* Leave Confirmation Dialog */}
      <DiscardChangesDialog
        visible={showLeaveDialog}
        onStay={cancelLeave}
        onLeave={confirmLeave}
        testId="PromptForm.Dialog.Leave"
      />
    </>
  );
};

export default PromptForm;
